import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import ApiService from "../../services/api-service";
import { showErrorToast, showSuccessToast } from "../../lib/toast";

// Must match REPORT_REASONS in backend/routes/feedback.js.
const REASONS = ["Spam", "Hateful or abusive", "Harassment", "Sexual content", "Violence", "Misinformation", "Copyright", "Other"];

export type ReportTarget = { postType: string; postId: number; commentId?: number };

// Lets anyone report a post or comment to the team (app-store user-generated
// content policies require it). The report is emailed to the feedback inbox.
export function ReportDialog({ target, onClose }: { target: ReportTarget | null; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!target) return;
    setReason("");
    setDetails("");
  }, [target]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!target || !reason) return;
    setIsSending(true);
    try {
      await ApiService.reportContent({ ...target, reason, details: details.trim() || undefined });
      showSuccessToast("Thanks for reporting. We'll review it.");
      onClose();
    } catch (error: any) {
      showErrorToast(error?.message || "Couldn't send the report. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const what = target?.commentId ? "comment" : "post";

  return (
    <Dialog open={Boolean(target)} onOpenChange={(open: boolean) => !open && !isSending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Report this {what}</DialogTitle>
          <DialogDescription>Tell us what's wrong. Reports go to The Deepam team for review.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <fieldset className="grid grid-cols-2 gap-2">
            <legend className="sr-only">Reason</legend>
            {REASONS.map((option) => (
              <label
                key={option}
                className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                  reason === option
                    ? "border-slate-800 bg-slate-800 text-white dark:border-slate-200 dark:bg-slate-200 dark:text-slate-900"
                    : "border-slate-200 text-slate-700 dark:border-slate-700 dark:text-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="report-reason"
                  value={option}
                  checked={reason === option}
                  onChange={() => setReason(option)}
                  className="sr-only"
                />
                {option}
              </label>
            ))}
          </fieldset>
          <Textarea
            id="report-details"
            value={details}
            onChange={(event) => setDetails(event.target.value)}
            maxLength={1000}
            placeholder="Anything else we should know? (optional)"
            className="min-h-[80px]"
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isSending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!reason || isSending}>
              {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              Send report
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
